// src/hooks/usePexels.js
import { useState, useCallback, useRef } from "react";
import {
  fetchAll, searchAll,
  fetchCurated, searchPexels,
  fetchWallhaven, searchWallhaven,
} from "../utils/api";

export const usePexels = () => {
  const [photos,       setPhotos]       = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [totalResults, setTotalResults] = useState(0);
  const [query,        setQuery]        = useState("");
  const [page,         setPage]         = useState(1);
  const [hasMore,      setHasMore]      = useState(true);
  const [source,       setSource]       = useState("all");
  const [orientation,  setOrientation]  = useState("");
  const [retrying,     setRetrying]     = useState(false);

  // Save last fetch function for retry
  const lastFetchRef = useRef(null);
  const abortRef     = useRef(null);

  const _load = useCallback(async (fetchFn, isNewSearch, newQuery = "", newSource = "all", newOrientation = "") => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // Save for retry
    lastFetchRef.current = { fetchFn, isNewSearch, newQuery, newSource, newOrientation };

    setLoading(true);
    setError(null);
    setRetrying(false);

    try {
      const { data } = await fetchFn();
      if (controller.signal.aborted) return;

      // Filter blank/broken images
      const validPhotos = (data.photos || []).filter(p =>
        p && p.src &&
        (p.src.large || p.src.medium || p.src.original) &&
        p.width > 0 && p.height > 0
      );

      setPhotos(prev => isNewSearch ? validPhotos : [...prev, ...validPhotos]);
      setTotalResults(data.total_results || 0);
      setQuery(newQuery);
      setSource(newSource);
      setOrientation(newOrientation);
      setPage(prev => isNewSearch ? 2 : prev + 1);
      setHasMore(!!data.next_page || validPhotos.length >= 12);
    } catch (err) {
      if (controller.signal.aborted) return;
      if (err.response?.status === 429) {
        setError("rate_limit");
      } else if (!err.response || err.code === "ECONNABORTED") {
        setError("server_sleep");
      } else {
        setError("general");
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  const toWallhavenRatio = (o) =>
    o === "landscape" ? "landscape" : o === "portrait" ? "portrait" : "";

  const loadCurated = useCallback((src = "all", orient = "") => {
    const ratio = toWallhavenRatio(orient);
    const fn =
      src === "pexels"    ? () => fetchCurated(null, 24, orient)
    : src === "wallhaven" ? () => fetchWallhaven(null, ratio)
    :                       () => fetchAll(null, orient);
    _load(fn, true, "", src, orient);
  }, [_load]);

  const search = useCallback((q, src = source, orient = orientation) => {
    if (!q.trim()) return;
    const ratio = toWallhavenRatio(orient);
    const fn =
      src === "pexels"    ? () => searchPexels(q.trim(), 1, 24, orient)
    : src === "wallhaven" ? () => searchWallhaven(q.trim(), 1, ratio)
    :                       () => searchAll(q.trim(), 1, orient);
    _load(fn, true, q.trim(), src, orient);
  }, [_load, source, orientation]);

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    const ratio = toWallhavenRatio(orientation);
    const fn = query
      ? (source === "pexels"    ? () => searchPexels(query, page, 24, orientation)
       : source === "wallhaven" ? () => searchWallhaven(query, page, ratio)
       :                          () => searchAll(query, page, orientation))
      : (source === "pexels"    ? () => fetchCurated(page, 24, orientation)
       : source === "wallhaven" ? () => fetchWallhaven(page, ratio)
       :                          () => fetchAll(page, orientation));
    _load(fn, false, query, source, orientation);
  }, [loading, hasMore, query, page, source, orientation, _load]);

  const switchSource = useCallback((newSource) => {
    if (query) search(query, newSource, orientation);
    else loadCurated(newSource, orientation);
  }, [query, orientation, search, loadCurated]);

  const switchOrientation = useCallback((newOrient) => {
    if (query) search(query, source, newOrient);
    else loadCurated(source, newOrient);
  }, [query, source, search, loadCurated]);

  // Retry last failed request
  const retry = useCallback(() => {
    if (!lastFetchRef.current) return;
    setRetrying(true);
    const { fetchFn, isNewSearch, newQuery, newSource, newOrientation } = lastFetchRef.current;
    _load(fetchFn, isNewSearch, newQuery, newSource, newOrientation);
  }, [_load]);

  return {
    photos, loading, error, retrying, totalResults,
    query, hasMore, source, orientation,
    loadCurated, search, loadMore,
    switchSource, switchOrientation, retry,
  };
};
